/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState, useRef } from 'react';
import { Socket } from 'socket.io-client';
import { useSelector, useDispatch } from 'react-redux';
import {
  Flex,
  Heading,
  Spacer,
  Button,
  Stack,
  Center,
  Text,
  Alert,
  AlertIcon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  useToast,
  Box,
} from '@chakra-ui/react';
import { DownloadIcon } from '@chakra-ui/icons';
import { useQuery } from 'react-query';
import { AppDispatch, RootState } from 'store';
import { Api } from 'api';
import {
  CustomerQueryCacheKey,
  ProductTypeQueryCacheKey,
  CustomerExpertiseFileType,
  ExpertisePackageAbilityDefinitionType,
  ProductType,
  ApplicationToggleType,
  ExpertiseDamageNoteType,
} from 'types';
import {
  DownloadAlert,
  GasketLeakResults,
  KmResults,
  LoadingText,
  NoteAutoCompleteSelect,
  AirbagCheckResults,
  MasterNotesBanner,
} from 'components';
import { useGetUploadedClientFile, useUploadClientFile, useCheckAbilityCompleted, useAuthUser } from 'core';
import { createCustomerSocket, createToggleSocket } from 'core/socket';
import { hideLoading, showLoading } from 'features';
import {
  ExpertiseBodyworkMotorControlSelector,
  ExpertiseBodyworkUndercarriageControlSelector,
} from 'apps/expertise/components';
import ExpertiseManuelTest from 'apps/expertise/components/ExpertiseManuelTest';
import { t, Trans } from '@lingui/macro';

export const ExpertiseMechanicInformationPage: React.FC = () => {
  const toast = useToast();
  const dispatch = useDispatch<AppDispatch>();
  const { isOpen: isKmResultModalOpen, onOpen: onKmResultModalOpen, onClose: onKmResultModalClose } = useDisclosure();
  const {
    isOpen: isKmResultWarningModalOpen,
    onOpen: onKmResultWarningModalOpen,
    onClose: onKmResultWarningModalClose,
  } = useDisclosure();
  const { isOpen: isManuelTestOpen, onOpen: onManuelTestOpen, onClose: onManuelTestClose } = useDisclosure();
  const {
    isOpen: isGasketLeakResultModalOpen,
    onOpen: onGasketLeakResultModalOpen,
    onClose: onGasketLeakResultModalClose,
  } = useDisclosure();
  const {
    isOpen: isMotorControlFormOpen,
    onOpen: onMotorControlFormOpen,
    onClose: onMotorControlFormClose,
  } = useDisclosure();
  const {
    isOpen: isUndercarriageControlFormOpen,
    onOpen: onUndercarriageControlFormOpen,
    onClose: onUndercarriageControlFormClose,
  } = useDisclosure();
  const {
    isOpen: isAirbagResultModalOpen,
    onOpen: onAirbagResultModalOpen,
    onClose: onAirbagResultModalClose,
  } = useDisclosure();

  const customer = useSelector((state: RootState) => state.customer.focusedCustomer);
  const canCustomerContinue = useSelector((state: RootState) => state.customer.canCustomerContinue);
  const customerAbilityDefinitionTable = useSelector(
    (state: RootState) => state.customer.customerAbilityDefinitionTable,
  );
  const testResultFileInputRef = useRef<HTMLInputElement>(null);
  const kmTestResultFileInputRef = useRef<HTMLInputElement>(null);
  const [isKmKaydiEnabled, setIsKmKaydiEnabled] = useState<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [customerSocket, setCustomerSocket] = useState<Socket | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [toggleSocket, setToggleSocket] = useState<Socket | null>(null);

  const authUserInfo = useAuthUser();

  const onUploadTestResultsClicked = (fileType: CustomerExpertiseFileType) => () => {
    let ref: React.RefObject<HTMLInputElement>;
    switch (fileType) {
      case CustomerExpertiseFileType.FaultDetectionResult:
        ref = testResultFileInputRef;
        break;
      case CustomerExpertiseFileType.KMDetectionResult:
        ref = kmTestResultFileInputRef;
        break;
      default:
        ref = testResultFileInputRef;
    }
    if (ref.current) {
      ref.current.click();
    }
  };

  const uploadTestResultFileMutation = useUploadClientFile(
    customer?.id,
    CustomerExpertiseFileType.FaultDetectionResult,
    {
      onMutate: () => {
        dispatch(showLoading());
      },
      onSettled: () => {
        dispatch(hideLoading());
        if (testResultFileInputRef.current) {
          testResultFileInputRef.current.value = '';
        }
        // eslint-disable-next-line no-use-before-define
        refetchFaultDetectionResultFileResponse();
        // eslint-disable-next-line no-use-before-define
        refetchIsUploadTestResultsCompleted();
      },
      onError: (error: any) => {
        console.error(error);
        toast({
          status: 'error',
          title: 'Dosya yüklenirken hata oluştu',
          isClosable: true,
        });
      },
    },
  );

  const uploadKMTestResultFileMutation = useUploadClientFile(
    customer?.id,
    CustomerExpertiseFileType.KMDetectionResult,
    {
      onMutate: () => {
        dispatch(showLoading());
      },
      onSettled: () => {
        dispatch(hideLoading());
        if (kmTestResultFileInputRef.current) {
          kmTestResultFileInputRef.current.value = '';
        }
        // eslint-disable-next-line no-use-before-define
        refetchKmTestDetectionResultFileResponse();
        // eslint-disable-next-line no-use-before-define
        refetchIsKmDetectionResultCompleted();
      },
      onError: (error: any) => {
        console.error(error);
        toast({
          status: 'error',
          title: 'Dosya yüklenirken hata oluştu',
          isClosable: true,
        });
      },
    },
  );

  const { data: gasketLeakTestResultFileResponse, refetch: refetchGasketLeakTestDetectionResultFileResponse } =
    useGetUploadedClientFile(customer?.id, CustomerExpertiseFileType.GasketLeakResult, {
      onError: (err: any) => {
        console.error(err);
        toast({
          title: 'Conta Kaçak Test sonuçları çekilirken hata oluştu',
          status: 'error',
        });
      },
    });

  const { data: airbagCheckResultFileResponse, refetch: refetchAirbagCheckResultFileResponse } =
    useGetUploadedClientFile(customer?.id, CustomerExpertiseFileType.AirbagCheckResult, {
      onError: (err: any) => {
        console.error(err);
        toast({
          title: 'Airbag Kontrol sonuçları çekilirken hata oluştu',
          status: 'error',
        });
      },
    });

  const { data: faultDetectionResultFileResponse, refetch: refetchFaultDetectionResultFileResponse } =
    useGetUploadedClientFile(customer?.id, CustomerExpertiseFileType.FaultDetectionResult, {
      onError: (err: any) => {
        console.error(err);
        toast({
          title: 'Araç testi sonuçları çekilirken hata oluştu',
          status: 'error',
        });
      },
    });

  const { data: kmTestResultFileResponse, refetch: refetchKmTestDetectionResultFileResponse } =
    useGetUploadedClientFile(customer?.id, CustomerExpertiseFileType.KMDetectionResult, {
      onError: (err: any) => {
        console.error(err);
        toast({
          title: 'KM testi sonuçları çekilirken hata oluştu',
          status: 'error',
        });
      },
    });

  const { data: isKmKaydiResultCompleted, refetch: refetchIsKmKaydiResultCompleted } = useCheckAbilityCompleted(
    customer?.id,
    ExpertisePackageAbilityDefinitionType.KMKaydiSorgulama,
  );

  const { data: isUploadTestResultsCompleted, refetch: refetchIsUploadTestResultsCompleted } = useCheckAbilityCompleted(
    customer?.id,
    ExpertisePackageAbilityDefinitionType.ArizaTespitSonuclari,
  );

  const { data: isKmDetectionResultCompleted, refetch: refetchIsKmDetectionResultCompleted } = useCheckAbilityCompleted(
    customer?.id,
    ExpertisePackageAbilityDefinitionType.KMTespitSonuclari,
  );

  const { data: isGasketLeakResultCompleted, refetch: refetchIsGasketLeakResultCompleted } = useCheckAbilityCompleted(
    customer?.id,
    ExpertisePackageAbilityDefinitionType.ContaKacakTespitSonuclari,
  );

  const { data: isMotorControlFormCompleted, refetch: refetchIsMotorControlFormCompleted } = useCheckAbilityCompleted(
    customer?.id,
    ExpertisePackageAbilityDefinitionType.MotorKontrolFormu,
  );

  const { data: isUndercarriageControlFormCompleted, refetch: refetchIsUndercarriageControlFormCompleted } =
    useCheckAbilityCompleted(customer?.id, ExpertisePackageAbilityDefinitionType.AltTakimKontrolFormu);

  const { data: isAirbagResultCompleted, refetch: refetchIsAirbagResultCompleted } = useCheckAbilityCompleted(
    customer?.id,
    ExpertisePackageAbilityDefinitionType.AirbagKontrol,
  );

  const {
    data: testResult,
    isLoading: isTestResultLoading,
    isError: isTestResultHasError,
    refetch: refetchHasExpertiseResultFromMachine,
  } = useQuery(
    CustomerQueryCacheKey.HasExpertiseResultFromMachine,
    async () => {
      try {
        if (customer?.id) {
          const response = await Api.Customer.hasExpertiseResultFromMachine(customer.id);
          return response.data;
        }
        return false;
      } catch (e) {
        throw Error();
      }
    },
    {
      onError: () => {
        toast({
          title: 'Test Sonuçları kontrolü sırasında hata oluştu',
          status: 'error',
        });
      },
      enabled: customer != null,
      refetchOnWindowFocus: false,
      cacheTime: 0,
    },
  );

  const { data: customerVehicle, isLoading } = useQuery(
    CustomerQueryCacheKey.GetCustomerVehicle,
    async () => {
      const response = await Api.Customer.getCustomerVehicle(customer!.id);
      if (!response.data) {
        return null;
      }
      const getCustomerVehicleResponse = response.data;
      return getCustomerVehicleResponse.customerVehicle;
    },
    {
      cacheTime: 0,
      retry: 0,
      enabled: customer != null,
      refetchOnWindowFocus: false,
    },
  );

  const { data: KmResultProductPrices } = useQuery(
    ProductTypeQueryCacheKey.GetKilometerQuery,
    async () => {
      const response = await Api.Util.getProductPriceByType(ProductType.KilometerQuery);
      return response.data;
    },
    {
      enabled: customer != null && isKmResultWarningModalOpen,
      refetchOnWindowFocus: false,
      cacheTime: 0,
      onError: (error: any) => {
        const {
          response: {
            data: { message },
          },
        } = error;
        toast({
          title: message,
          status: 'error',
          isClosable: true,
        });
      },
    },
  );

  useQuery(
    ['getToggleEnabledKMKaydi', ApplicationToggleType.KmQuery],
    async () => {
      const response = await Api.Util.getToggleEnabled(ApplicationToggleType.KmQuery);
      return response?.data;
    },
    {
      onSuccess: (data) => {
        setIsKmKaydiEnabled(data?.isEnabled);
      },
      onError: () => {
        toast({
          title: 'Şu an oto sorgudaki arıza sebebi ile km sorgulama işleminiz yapılmamıştır',
          status: 'error',
        });
      },
      refetchOnWindowFocus: false,
    },
  );

  const onKmResultButtonClicked = () => {
    onKmResultWarningModalOpen();
  };

  useEffect(() => {
    if (customer) {
      const newCustomerSocket = createCustomerSocket(customer.id.toString());
      setCustomerSocket(newCustomerSocket);

      newCustomerSocket.on(
        'customerAbilityCompleted',
        ({ customerId, abilityDefinitionType }: { customerId: number; abilityDefinitionType: number }) => {
          if (Number(customerId) === customer.id) {
            if (abilityDefinitionType === ExpertisePackageAbilityDefinitionType.KMKaydiSorgulama) {
              refetchIsKmKaydiResultCompleted();
            }
            if (abilityDefinitionType === ExpertisePackageAbilityDefinitionType.ArizaTespitSonuclari) {
              refetchIsUploadTestResultsCompleted();
            }
            if (abilityDefinitionType === ExpertisePackageAbilityDefinitionType.KMTespitSonuclari) {
              refetchIsKmDetectionResultCompleted();
            }
            if (abilityDefinitionType === ExpertisePackageAbilityDefinitionType.ContaKacakTespitSonuclari) {
              refetchIsGasketLeakResultCompleted();
            }
            if (abilityDefinitionType === ExpertisePackageAbilityDefinitionType.MotorKontrolFormu) {
              refetchIsMotorControlFormCompleted();
            }
            if (abilityDefinitionType === ExpertisePackageAbilityDefinitionType.AltTakimKontrolFormu) {
              refetchIsUndercarriageControlFormCompleted();
            }
            if (abilityDefinitionType === ExpertisePackageAbilityDefinitionType.AirbagKontrol) {
              refetchIsAirbagResultCompleted();
            }
          }
        },
      );

      return () => {
        newCustomerSocket.disconnect();
      };
    }
    return undefined;
  }, [customer]);

  useEffect(() => {
    const newToggleSocket = createToggleSocket();
    setToggleSocket(newToggleSocket);

    newToggleSocket.on('toggleChanged', (data: { type: ApplicationToggleType; isEnabled: boolean }) => {
      if (data?.type === ApplicationToggleType.KmQuery) {
        setIsKmKaydiEnabled(data?.isEnabled);
      }
    });

    return () => {
      newToggleSocket.disconnect();
    };
  }, []);

  if (isLoading || !customer) {
    return (
      <Center>
        <LoadingText />
      </Center>
    );
  }
  return (
    <>
      {customerVehicle && customerVehicle.chassisNumber && KmResultProductPrices && (
        <>
          <KmResults
            customerId={customer?.id}
            isOpen={isKmResultModalOpen}
            onClose={() => {
              onKmResultModalClose();
              refetchIsKmKaydiResultCompleted();
            }}
          />
          <Modal isOpen={isKmResultWarningModalOpen} onClose={onKmResultWarningModalClose} isCentered>
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>
                <Trans>KM Kaydı Sorgu Onay?</Trans>
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <Text>
                  <Trans>
                    KM Sorgusu için ₺
                    {KmResultProductPrices.buyPrice > 0 ? KmResultProductPrices.buyPrice : KmResultProductPrices.price}{' '}
                    sorgu bedeli kullanılacaktır. Devam etmek istiyor musunuz ?
                  </Trans>
                </Text>
              </ModalBody>
              <ModalFooter>
                <Button
                  colorScheme="blue"
                  mr={3}
                  onClick={() => {
                    onKmResultModalOpen();
                    onKmResultWarningModalClose();
                  }}
                >
                  <Trans>Evet</Trans>
                </Button>
                <Button onClick={onKmResultWarningModalClose}>
                  <Trans>Hayır</Trans>
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        </>
      )}

      {customer && (
        <>
          <GasketLeakResults
            customerId={customer?.id}
            isOpen={isGasketLeakResultModalOpen}
            onClose={() => {
              onGasketLeakResultModalClose();
              refetchGasketLeakTestDetectionResultFileResponse();
              refetchIsGasketLeakResultCompleted();
            }}
          />
          <ExpertiseBodyworkMotorControlSelector
            isOpen={isMotorControlFormOpen}
            onClose={() => {
              onMotorControlFormClose();
              refetchIsMotorControlFormCompleted();
            }}
            isDisabled={!canCustomerContinue}
          />
          <ExpertiseBodyworkUndercarriageControlSelector
            isOpen={isUndercarriageControlFormOpen}
            onClose={() => {
              onUndercarriageControlFormClose();
              refetchIsUndercarriageControlFormCompleted();
            }}
            isDisabled={!canCustomerContinue}
          />
          <AirbagCheckResults
            customerId={customer?.id}
            isOpen={isAirbagResultModalOpen}
            onClose={() => {
              onAirbagResultModalClose();
              refetchAirbagCheckResultFileResponse();
              refetchIsAirbagResultCompleted();
            }}
          />
          <ExpertiseManuelTest
            isOpen={isManuelTestOpen}
            customerId={customer.id}
            chassisNumber={customerVehicle?.chassisNumber}
            refetchHasExpertiseResultFromMachine={refetchHasExpertiseResultFromMachine}
            isDisabled={!canCustomerContinue && authUserInfo?.isSuperAdmin === true}
            plate={customer.plate}
            onClose={() => {
              onManuelTestClose();
            }}
          />
        </>
      )}
      <input
        type="file"
        accept="application/pdf"
        hidden
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          if (e.target.files && e.target.files.length === 1) {
            uploadTestResultFileMutation.mutate(e.target.files[0]);
          }
        }}
        ref={testResultFileInputRef}
      />
      <input
        type="file"
        accept="application/pdf"
        hidden
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          if (e.target.files && e.target.files.length === 1) {
            uploadKMTestResultFileMutation.mutate(e.target.files[0]);
          }
        }}
        ref={kmTestResultFileInputRef}
      />
      <Flex align="stretch" direction="column" basis="100%" gap="2">
        <Heading as="h4" size="md">
          <Trans>Mekanik İnceleme</Trans>
        </Heading>
        <Spacer />
        <MasterNotesBanner
          brandId={customerVehicle?.brandId}
          modelId={customerVehicle?.modelId}
          modelYear={customerVehicle?.modelYear}
          noteType={ExpertiseDamageNoteType.Mechanic}
        />
        {faultDetectionResultFileResponse && (
          <DownloadAlert
            href={faultDetectionResultFileResponse.url}
            label={t`Arıza Tespit Sonucu İndir`}
            icon={<DownloadIcon />}
          />
        )}
        {kmTestResultFileResponse && (
          <DownloadAlert
            href={kmTestResultFileResponse.url}
            label={t`KM Tespit Sonucu İndir`}
            icon={<DownloadIcon />}
          />
        )}

        {gasketLeakTestResultFileResponse && (
          <DownloadAlert
            href={gasketLeakTestResultFileResponse.url}
            label={t`Conta Kaçak Test Sonucu İndir`}
            icon={<DownloadIcon />}
          />
        )}
        {airbagCheckResultFileResponse && (
          <DownloadAlert
            href={airbagCheckResultFileResponse.url}
            label={t`Airbag Kontrol Sonucu İndir`}
            icon={<DownloadIcon />}
          />
        )}

        {(!isTestResultLoading || isTestResultHasError) &&
          customerAbilityDefinitionTable[ExpertisePackageAbilityDefinitionType.EkspertizCihaziTestSonuclari] && (
            <Alert status={testResult ? 'success' : 'error'}>
              <AlertIcon />
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: '10px',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                }}
              >
                {testResult ? t`Test verileri yüklü` : t`Test verileri yüklü değil`}
                <Button
                  onClick={() => {
                    onManuelTestOpen();
                  }}
                >
                  {testResult ? t`Düzenle` : t`Manuel Ekle`}
                </Button>
              </Box>
            </Alert>
          )}

        <Stack direction={['column', 'column', 'column', null, 'row']} spacing="24px">
          <Button
            border={isKmKaydiResultCompleted ? '2px solid green' : 'none'}
            onClick={onKmResultButtonClicked}
            hidden={
              !customerVehicle?.chassisNumber ||
              !customerAbilityDefinitionTable[ExpertisePackageAbilityDefinitionType.KMKaydiSorgulama] ||
              !isKmKaydiEnabled
            }
          >
            <Trans>KM Sorgu</Trans>
          </Button>
          <Button
            border={isUploadTestResultsCompleted ? '2px solid green' : 'none'}
            hidden={!customerAbilityDefinitionTable[ExpertisePackageAbilityDefinitionType.ArizaTespitSonuclari]}
            onClick={onUploadTestResultsClicked(CustomerExpertiseFileType.FaultDetectionResult)}
          >
            <Trans>OBD Raporu Yükle</Trans>
          </Button>
          <Button
            border={isKmDetectionResultCompleted ? '2px solid green' : 'none'}
            hidden={!customerAbilityDefinitionTable[ExpertisePackageAbilityDefinitionType.KMTespitSonuclari]}
            onClick={onUploadTestResultsClicked(CustomerExpertiseFileType.KMDetectionResult)}
          >
            <Trans>OBD KM Raporu Yükle</Trans>
          </Button>
          <Button
            border={isGasketLeakResultCompleted ? '2px solid green' : 'none'}
            hidden={!customerAbilityDefinitionTable[ExpertisePackageAbilityDefinitionType.ContaKacakTespitSonuclari]}
            onClick={() => {
              onGasketLeakResultModalOpen();
            }}
          >
            <Trans>Conta Kaçak Testi</Trans>
          </Button>
          <Button
            border={isMotorControlFormCompleted ? '2px solid green' : 'none'}
            hidden={!customerAbilityDefinitionTable[ExpertisePackageAbilityDefinitionType.MotorKontrolFormu]}
            onClick={onMotorControlFormOpen}
          >
            <Trans>Motor Kontrolleri</Trans>
          </Button>
          <Button
            border={isUndercarriageControlFormCompleted ? '2px solid green' : 'none'}
            hidden={!customerAbilityDefinitionTable[ExpertisePackageAbilityDefinitionType.AltTakimKontrolFormu]}
            onClick={onUndercarriageControlFormOpen}
          >
            <Trans>Alt Takım Kontrolleri</Trans>
          </Button>
          <Button
            border={isAirbagResultCompleted ? '2px solid green' : 'none'}
            hidden={!customerAbilityDefinitionTable[ExpertisePackageAbilityDefinitionType.AirbagKontrol]}
            onClick={onAirbagResultModalOpen}
          >
            <Trans>Airbag Kontrolü</Trans>
          </Button>
        </Stack>
        <Spacer />

        <NoteAutoCompleteSelect
          customerId={customer.id}
          noteType={ExpertiseDamageNoteType.Mechanic}
          isDisabled={
            !canCustomerContinue ||
            !customerAbilityDefinitionTable[ExpertisePackageAbilityDefinitionType.MotorMekanikKontrolNotlari]
          }
        />
      </Flex>
    </>
  );
};
